import FullWidthSection from '@/components/full-width-section';
import ImageOptimizer from '@/components/image-optimizer';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import { cn } from '@/lib/utils';
import { useBannerList } from '@/services/query/banner.query';
import { BannerPosition } from '@/services/types/banner.type';
import Autoplay from 'embla-carousel-autoplay';
import Link from 'next/link';
import { useEffect, useState } from 'react';

const EventBannerSection = () => {
  const [mobileEmblaApi, setMobileEmblaApi] = useState<CarouselApi>();
  const [pcEmblaApi, setPcEmblaApi] = useState<CarouselApi>();
  const [mobileSelectedIndex, setMobileSelectedIndex] = useState(0);

  useEffect(() => {
    if (!mobileEmblaApi) return;
    const onSelect = () =>
      setMobileSelectedIndex(mobileEmblaApi.selectedScrollSnap());
    mobileEmblaApi.on('select', onSelect);
    onSelect(); // 초기값 설정
    return () => {
      mobileEmblaApi.off('select', onSelect);
    };
  }, [mobileEmblaApi]);

  const { data: bannerList } = useBannerList({
    position: BannerPosition.MIDDLE,
  });
  const pcMiddleBanners = bannerList?.data?.pc ?? [];
  const mobileMiddleBanners = bannerList?.data?.mobile ?? [];

  if (pcMiddleBanners.length === 0 || mobileMiddleBanners.length === 0)
    return null;

  return (
    <FullWidthSection className="bg-level-1 desktop:py-25 overflow-visible py-16 text-white">
      <div className="desktop:web-w desktop:mx-auto w-full text-center">
        {/* 타이틀 */}
        <p className="typo-28 desktop:typo-40 desktop:font-medium desktop:mb-12 mb-6 font-extrabold text-white">
          지금 받을 수 있는{' '}
          <span className="desktop:font-extrabold">이벤트 혜택!</span>
        </p>

        {/* 배너 캐러셀 */}
        {/**모바일 */}
        <div className="desktop:hidden px-6">
          <div className="relative">
            <Carousel
              setApi={setMobileEmblaApi}
              opts={{ align: 'start', loop: true }}
              plugins={[Autoplay({ delay: 3000, stopOnInteraction: false })]}
            >
              <CarouselContent className="flex gap-x-2">
                {mobileMiddleBanners.map((banner) => (
                  <CarouselItem
                    key={banner.id}
                    className="w-full flex-shrink-0"
                  >
                    <Link href={banner.href || ''}>
                      <div className="relative aspect-[452/200] w-full overflow-hidden rounded-lg">
                        <ImageOptimizer
                          src={banner.upfilePath}
                          alt={banner.title}
                          fill
                        />
                      </div>
                    </Link>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
          <div className="mt-5 flex justify-center gap-x-1.5">
            {mobileMiddleBanners.map((_, i) => (
              <span
                key={i}
                className={cn(
                  'bg-level-4 inline-block size-1.5 rounded-full',
                  mobileSelectedIndex === i ? 'border border-white' : '',
                )}
              ></span>
            ))}
          </div>
        </div>

        {/**웹 */}
        <div className="desktop:block relative hidden">
          <Carousel
            setApi={setPcEmblaApi}
            opts={{ align: 'start', loop: true }}
            plugins={[Autoplay({ delay: 3000, stopOnInteraction: false })]}
          >
            <CarouselContent className="flex gap-x-2">
              {pcMiddleBanners.map((banner) => (
                <CarouselItem key={banner.id} className="w-full flex-shrink-0">
                  <Link href={banner.href || ''}>
                    <div className="relative aspect-[1180/200] w-full overflow-hidden rounded-[14px]">
                      <ImageOptimizer
                        src={banner.upfilePath}
                        alt={banner.title}
                        fill
                      />
                    </div>
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <Button
            onClick={() => pcEmblaApi?.scrollPrev()}
            disabled={!pcEmblaApi}
            className="absolute top-1/2 left-0 z-1 -ml-10 -translate-y-1/2 transform shadow-none"
          >
            <ImageOptimizer
              src="/images/icons/icon_slide_left.svg"
              alt="이전 슬라이드"
              width={55}
              height={55}
              className="cursor-pointer rounded-full hover:bg-gray-100"
            />
          </Button>
          <Button
            onClick={() => pcEmblaApi?.scrollNext()}
            disabled={!pcEmblaApi}
            className="absolute top-1/2 right-0 z-1 -mr-10 -translate-y-1/2 transform shadow-none"
          >
            <ImageOptimizer
              src="/images/icons/icon_slide_right.svg"
              alt="다음 슬라이드"
              width={55}
              height={55}
              className="cursor-pointer rounded-full hover:bg-gray-100"
            />
          </Button>
        </div>
      </div>
    </FullWidthSection>
  );
};

export default EventBannerSection;
